import { useCallback, useEffect, useState } from "react";
import { Clock, Loader2, LogIn, LogOut } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtClock, fmtDate, fmtDuration, shiftLabel, StatusPill } from "@/components/staff/attendance/utils";

const LiveClock = () => {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return <div className="font-serif font-bold text-4xl tabular-nums" data-testid="attendance-live-clock">{now.toLocaleTimeString("id-ID", { timeZone: "Asia/Jakarta" })}</div>;
};

export const MyAttendance = ({ config }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/attendance/me").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (path, msg, body = {}) => {
    setBusy(true);
    try {
      await api.post(path, body);
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <Loader2 className="w-6 h-6 animate-spin text-forest" />;
  const { user, open, history, today } = data;
  const shift = config.shifts.find((s) => s.id === today.shift);

  return (
    <div className="grid lg:grid-cols-[360px_1fr] gap-5">
      <div className="bg-forest text-paper rounded-2xl p-6" data-testid="my-attendance-card">
        <div className="flex items-center gap-2 text-sm opacity-85"><Clock className="w-4 h-4" /> Waktu sekarang (WIB)</div>
        <LiveClock />
        <div className="mt-4 text-sm space-y-1" data-testid="my-attendance-assignment">
          <div>{user.name}</div>
          <div className="opacity-85">{user.division || "Divisi belum diatur"} · Hari ini: {shift ? `Shift ${shift.label} (${shift.start}–${shift.end})` : today.shift === "libur" ? "Libur" : "Shift belum diatur"}{today.source === "tukar" && " · hasil tukar shift"}</div>
        </div>
        {open ? (
          <>
            <div className="mt-4 text-sm bg-white/10 rounded-lg px-3 py-2" data-testid="my-attendance-open">Masuk {fmtClock(open.clock_in)} · {shiftLabel(config, open.shift)} {open.status === "terlambat" ? `· terlambat ${open.late_minutes} mnt` : "· tepat waktu"}</div>
            <button onClick={() => act("/attendance/clock-out", "Absen pulang tercatat")} disabled={busy} className="w-full mt-4 py-3.5 rounded-xl bg-krem text-ink font-semibold flex items-center justify-center gap-2 hover:bg-white transition-colors" data-testid="attendance-clock-out-button">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogOut className="w-4 h-4" />} Absen Pulang
            </button>
          </>
        ) : (
          <button onClick={() => act("/attendance/clock-in", "Absen masuk tercatat")} disabled={busy} className="w-full mt-5 py-3.5 rounded-xl bg-krem text-ink font-semibold flex items-center justify-center gap-2 hover:bg-white transition-colors disabled:opacity-60" data-testid="attendance-clock-in-button">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />} Absen Masuk
          </button>
        )}
        <p className="text-xs opacity-75 mt-3">Absen mengikuti jadwal shift. Perlu shift lain? Ajukan tukar shift di bawah. Absen dibuka 60 menit sebelum shift. Lewat {config.grace_minutes} menit dihitung terlambat.</p>
      </div>
      <div className="card-paper p-5">
        <h3 className="font-semibold">Riwayat absensi saya</h3>
        {history.length === 0 && <p className="text-sm text-muted2 mt-3" data-testid="my-attendance-empty">Belum ada riwayat.</p>}
        <div className="divide-y divide-sage-200 mt-2">
          {history.map((h) => (
            <div key={h.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5 text-sm" data-testid={`my-attendance-row-${h.id}`}>
              <span>{fmtDate(h.date)} · {shiftLabel(config, h.shift)}{(h.swap || (h.assigned_shift && h.assigned_shift !== h.shift)) && <span className="text-xs text-amber-700"> (tukar shift)</span>}</span>
              <span className="text-muted2">{fmtClock(h.clock_in)} – {h.clock_out ? fmtClock(h.clock_out) : "…"} {h.clock_out && `(${fmtDuration(h.duration_minutes)})`}</span>
              <StatusPill rec={h} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
